import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
} from "react";
import { checkEmailRegEx } from "utils/regEx";
import useStatusMutation from "hook/useStatusMutation";
import { postCheckEmail, postLogin } from "api/authAPI";
import {
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import {
  LOGIN_STEP,
  LoginInfo,
} from "../LoginContainer";
import {
  GetLoginResponse,
  PostLoginRequest,
  PostLoginResponse,
} from "api/interfaces/authInterface.interface";
import { checkEmpty, getClientDevice, toTrim } from "utils/functions";
import { CustomError } from "api/interfaces/commonInterface.interface";
import { useLocalStorage } from "hook/useLocalStorage";
import useApiError from "hook/useApiError";
import { RegisterPasskey } from "./passkey/RegisterPasskey";
import { LoginPasskey } from "./passkey/LoginPasskey";
import InputPassword from "./email/InputPassword";
import InputEmail from "./email/InputEmail";
import ResellerPage from "../reseller";
import { clearAxiosHeaders } from "utils/AuthUtil";

interface Props {
  loginInfo: LoginInfo;
  setLoginInfo: Dispatch<SetStateAction<LoginInfo>>;
  isValidated: Validate;
  setIsValidated: Dispatch<SetStateAction<Validate>>;
  validateMsg: ValidateMsg;
  setValidateMsg: Dispatch<SetStateAction<ValidateMsg>>;
  step: LOGIN_STEP;
  onChangeStep: (step: LOGIN_STEP) => void;
  onLoginSuccess: (data: GetLoginResponse | PostLoginResponse) => void;
}

export default function LoginMyDwContainer({
  loginInfo,
  setLoginInfo,
  isValidated,
  setIsValidated,
  validateMsg,
  setValidateMsg,
  step,
  onChangeStep,
  onLoginSuccess,
}: Props) {
  //로컬스토리지 저장.
  const [emailLocal, setEmailLocal] = useLocalStorage("email", "");
  const [rememberLocal, setRememberLocal] = useLocalStorage("remember", false);
  const [rememberInfo, setRememberInfo] = useState<{
    email: string;
    isRemember: boolean;
  }>(() => ({
    email: emailLocal === loginInfo.email && rememberLocal ? emailLocal : "",
    isRemember: emailLocal === loginInfo.email && rememberLocal,
  }));
  const { displayError, intlString, lang, getErrorMessage } = useApiError();
  const onChangeLoginInfo = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.name === "email") {
        setLoginInfo((info) => ({
          ...info,
          email:
            e.target.value !== undefined && e.target.value !== ""
              ? toTrim(e.target.value)
              : "",
        }));
      }

      if (e.target.name === "password") {
        setLoginInfo((info) => ({
          ...info,
          password: e.target.value,
        }));
      }
    },
    []
  );
  const onChangeRemember = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRememberInfo((rememberInfo) => {
        return {
          ...rememberInfo,
          isRemember: e.target.checked,
        };
      });
      setRememberLocal(e.target.checked);
      setEmailLocal(
        e.target.checked ? loginInfo.email.replace(/^\s+|\s+$/gm, "") : ""
      );
    },
    [loginInfo.email]
  );
  const mutationCheckEmail = useStatusMutation(postCheckEmail, {
    onSuccess: (res: any) => {
      if (
        res.result.provider !== undefined &&
        res.result.provider !== "AUTH0"
      ) {
        setIsValidated({ ...isValidated, email: true });
        setValidateMsg({
          ...validateMsg,
          email: intlString(lang.validateMsg.signup.checkEmail.cloudUser),
        });
        return;
      }

      if (res.result.isExist) {
        //Check Registered Email
        if (res.result.isActivate) {
          //Check SingUp User
          if (res.result?.hasAccount === undefined || !res.result?.hasAccount) {
            setIsValidated({ ...isValidated, email: true });
            setValidateMsg({
              ...validateMsg,
              email: intlString(lang.validateMsg.signup.accountNotFound),
            });
          } else {
            if (step === LOGIN_STEP.SETUP_PASSKEY) {
              onClickLogin();
            } else {
              onChangeStep(LOGIN_STEP.INPUT_PASSWORD);
            }
          }
        } else {
          setIsValidated({ ...isValidated, email: true });
          setValidateMsg({
            ...validateMsg,
            email: intlString(lang.validateMsg.signup.disActivateUser),
          });
        }
      } else {
        setIsValidated({ ...isValidated, email: true });
        setValidateMsg({
          ...validateMsg,
          email: intlString(lang.validateMsg.signup.checkEmail.noExist),
        });
      }
    },
    onError: (err: CustomError) => {
      setIsValidated({ ...isValidated, email: true });
      setValidateMsg({
        ...validateMsg,
        email: getErrorMessage(err, lang.validateMsg.signup.defaultError),
      });
    },
  });
  const validateEmail = useCallback(() => {
    if (checkEmpty(loginInfo.email)) {
      setIsValidated({ ...isValidated, email: true });
      setValidateMsg({
        ...validateMsg,
        email: intlString(lang.validateMsg.profile.email.empty),
      });
      return false;
    }

    if (!checkEmailRegEx(loginInfo.email)) {
      setIsValidated({ ...isValidated, email: true });
      setValidateMsg({
        ...validateMsg,
        email: intlString(lang.validateMsg.signup.invalidEmail),
      });
      return false;
    }
    try {
      clearAxiosHeaders();
    } catch {
      console.error("cant not delete Authorization");
      return false;
    }
    return true;
  }, [
    intlString,
    isValidated,
    lang.validateMsg.profile.email.empty,
    lang.validateMsg.signup.invalidEmail,
    loginInfo.email,
    setIsValidated,
    setValidateMsg,
    validateMsg,
  ]);
  const onClickCheckEmail = useCallback(async () => {
    if (!mutationCheckEmail.idle) return;
    const result = validateEmail();
    if (!result) return;
    setLoginInfo((info) => ({
      ...info,
      password: "",
    }));
    setIsValidated({ ...isValidated, password: false });
    setValidateMsg({
      ...validateMsg,
      password: "",
    });

    setEmailLocal(
      rememberInfo.isRemember ? loginInfo.email.replace(/^\s+|\s+$/gm, "") : ""
    );
    mutationCheckEmail.mutate({
      email: loginInfo.email,
    });
  }, [
    isValidated,
    loginInfo.email,
    mutationCheckEmail,
    rememberInfo.isRemember,
    setEmailLocal,
    setIsValidated,
    setLoginInfo,
    setValidateMsg,
    validateEmail,
    validateMsg,
  ]);
  const mutationLogin = useStatusMutation(postLogin, {
    onSuccess: (data: PostLoginResponse) => {
      onLoginSuccess(data);
    },
    onError: (err: CustomError) => {
      console.log("LoginMyDwContainer error", err);
      setIsValidated({ ...isValidated, password: true });
      setLoginInfo({
        ...loginInfo,
        password: "",
      });
      setValidateMsg({
        ...validateMsg,
        password: displayError(
          err as CustomError,
          intlString(lang.validateMsg.signup.loginIncorrect)
        ),
      });
    },
  });
  const onClickLogin = useCallback(() => {
    if (!mutationLogin.idle) return;
    let payload: PostLoginRequest = {
      email: loginInfo.email,
      password: loginInfo.password,
      device: getClientDevice(),
    };
    //30일 미인증 2fa 인증번호 확인
    const remember2FaLocal = localStorage.getItem(
      "remember2fa_" + loginInfo.email
    );
    if (remember2FaLocal) {
      payload.remember2fa = remember2FaLocal;
    }
    mutationLogin.mutate(payload);
  }, [mutationLogin, loginInfo.email, loginInfo.password]);
  const onClickRegisterPasskey = useCallback(() => {
    if (!mutationCheckEmail.idle) return;
    if (!mutationLogin.idle) return;
    try {
      const result = validateEmail();
      if (!result) return;
    } catch (error) {
      console.error(error);
      return;
    }
    if (checkEmpty(loginInfo.password)) {
      setIsValidated({ ...isValidated, password: true });
      setValidateMsg({
        ...validateMsg,
        password: intlString(lang.validateMsg.signup.password.empty),
      });
      return;
    }
    mutationCheckEmail.mutate({
      email: loginInfo.email,
    });
  }, [
    intlString,
    isValidated,
    lang.validateMsg.signup.password.empty,
    loginInfo.email,
    loginInfo.password,
    mutationCheckEmail,
    mutationLogin.idle,
    setIsValidated,
    setValidateMsg,
    validateEmail,
    validateMsg,
  ]);

  if (step === LOGIN_STEP.INPUT_PASSWORD) {
    return (
      <InputPassword
        loginInfo={loginInfo}
        isValidated={isValidated}
        validateMsg={validateMsg}
        onChangeLoginInfo={onChangeLoginInfo}
        onChangeStep={onChangeStep}
        handleClick={onClickLogin}
        step={step}
      />
    );
  }
  if (step === LOGIN_STEP.LOGIN_WITH_PASSKEY) {
    return (
      <LoginPasskey
        onLoginSuccess={onLoginSuccess}
        onChangeStep={onChangeStep}
      />
    );
  }
  if (step === LOGIN_STEP.SETUP_PASSKEY) {
    return (
      <RegisterPasskey
        loginInfo={loginInfo}
        isValidated={isValidated}
        validateMsg={validateMsg}
        onChangeLoginInfo={onChangeLoginInfo}
        onChangeStep={onChangeStep}
        onClickRegisterPasskey={onClickRegisterPasskey}
        step={step}
      />
    );
  }
  if (step === LOGIN_STEP.RESELLER) {
    return (
      <ResellerPage
        loginInfo={loginInfo}
        isValidated={isValidated}
        validateMsg={validateMsg}
        onChangeStep={onChangeStep}
        onChangeLoginInfo={onChangeLoginInfo}
        validateEmail={validateEmail}
        setIsValidated={setIsValidated}
        setValidateMsg={setValidateMsg}
      />
    );
  }
  return (
    <InputEmail
      loginInfo={loginInfo}
      isValidated={isValidated}
      validateMsg={validateMsg}
      disabled={false}
      onChangeLoginInfo={onChangeLoginInfo}
      onChangeRemember={onChangeRemember}
      checkedRemember={rememberInfo.isRemember}
      onChangeStep={onChangeStep}
      handleClick={onClickCheckEmail}
    />
  );
}
